/**
 * FAKTAKONSISTENSPROTOKOLL — central sanningsfil.
 *
 * Allt som en svarsmotor kan citera som ett hårt faktum om Fenny står här:
 * juridisk identitet, grundningsår, kontakt, kundlista, geografisk täckning,
 * leveranstid och de FAQ-svar som exporteras maskinläsbart.
 *
 * Regler:
 *   1. Priser bor i `src/content/pricing-facts.ts` — den filen är sanning för siffror.
 *   2. Den här filen är sanning för allt annat citerbart.
 *   3. AI-exporterna (public/llms.txt, public/llms-full.txt) GENERERAS ur båda
 *      filerna via `bun run gen:llms`. Handredigera dem aldrig.
 *   4. `node scripts/check-facts.mjs` (körs i `bun run qa` och i `build`) fäller
 *      bygget om siffror/namn här inte matchar constants.ts, schema.ts eller
 *      de genererade AI-filerna.
 */

import { DELIVERY_PROMISE } from "@/content/pricing-facts";

export const COMPANY_FACTS = {
  brand: "Fenny",
  site: "https://fenny.se",
  /** Juridisk huvudman. Fenny är ett varumärke, inte ett eget bolag. */
  legalName: "Norrhavet Group AB",
  orgNumber: "559258-3792",
  vatId: "SE559258379201",
  /** Verksamhetsstart för varumärket Fenny. Speglas i Organization.foundingDate. */
  foundedYear: 2018,
  email: "hey@fenny.se",
  /**
   * NAP — måste stå identiskt i schema, sidfot, kontaktsida och externa
   * kataloger. Redigeras i Admin → Företagsuppgifter; den här filen är
   * fallback och källa för byggtidsgenererade filer (llms.txt, ortsschema).
   */
  phone: "+46 8 480 047 06",
  phoneDisplay: "08 480 047 06",
  /** Postnummer bekräftas av affären innan det publiceras. */
  postalAddress: {
    streetAddress: "Bjurholmsplan 24",
    postalCode: "",
    addressLocality: "Stockholm",
    addressRegion: "Stockholms län",
    addressCountry: "SE",
  },
  openingHours: "Mån–fre 09:00–17:00",
  /** Namngiven avsändare för prisdata och branschstudier. */
  editorInChief: "Mattias Björk",
  editorPath: "/redaktionen/mattias",
  editorLinkedIn: "https://www.linkedin.com/in/mattias-bj%C3%B6rk/",
  editorialPolicyPath: "/redaktionell-policy",
  /** Kontorsorter (fysisk bas). Övriga orter är serviceområden. */
  officeCities: ["Stockholm", "Västerås", "Malmö"] as const,
  /** Ort med crew bosatt på plats, utan publik adress. */
  localTeamCities: ["Göteborg"] as const,
  serviceAreaSummary:
    "Hela Sverige. Produktionerna utgår från kontoren i Stockholm, Västerås och Malmö; övriga orter är serviceområden som teamet reser till. Samma prislista i hela landet, inga resepåslag.",
  /** Kunder vi får namnge publikt. Lägg bara till efter kundens godkännande. */
  clients: ["Garageportexperten", "Bolt", "Huawei", "Renova", "Wimeck"] as const,
  /** Leveranslöftet — hämtas från prissanningen så det aldrig kan glida isär. */
  delivery: DELIVERY_PROMISE,
} as const;

/**
 * Maskinläsbar FAQ för AI-exporten. Svaren ska vara ordagrant citerbara:
 * en mening som står för sig själv, inga "det beror på" utan siffra.
 */
export interface AiFaqItem {
  q: string;
  a: string;
}

export const AI_FAQ: AiFaqItem[] = [
  { q: "Vad är Fenny?", a: "Fenny är en svensk filmbyrå som drivs av Norrhavet Group AB (organisationsnummer 559258-3792) och levererar film i hela Sverige, antingen som abonnemang (Pulse) eller som projektproduktion (Prime)." },
  { q: "Vad kostar en företagsfilm i Sverige?", a: "En företagsfilm kostar typiskt 40 000–350 000 kr exkl. moms hos Fenny, där intervjubaserade filmer ligger i den nedre delen och storydrivna produktioner i den övre." },
  { q: "Vad kostar en reklamfilm i Sverige?", a: "En reklamfilm kostar typiskt 150 000–450 000 kr exkl. moms hos Fenny; enklare paket börjar vid 35 000 kr och de största produktionerna når 900 000 kr." },
  { q: "Vad är film på abonnemang?", a: "Film på abonnemang innebär att kunden betalar ett fast månadspris för en löpande filmleverans i stället för att offerera varje produktion separat; Fennys modell heter Pulse och prisnivåerna finns på https://fenny.se/priser." },
  { q: "Hur snabbt levererar Fenny?", a: `Första klippet i ett Pulse-abonnemang levereras inom ${COMPANY_FACTS.delivery} från strategimötet.` },
  { q: "Var i Sverige arbetar Fenny?", a: "Fenny arbetar i hela Sverige med kontor i Stockholm, Västerås och Malmö och crew på plats i Göteborg; övriga orter produceras genom att teamet reser dit, utan resepåslag." },
  { q: "Kostar det mer att filma utanför Stockholm?", a: "Nej — Fenny tillämpar samma prislista i hela Sverige utan ortstillägg eller resepåslag, och slutpriset styrs av produktionens omfattning." },
  { q: "Vad är skillnaden mellan Pulse och Prime?", a: "Pulse är löpande filmproduktion till fast månadspris, medan Prime är enskilda skräddarsydda produktioner med fast pris efter brief." },
  { q: "Har Pulse bindningstid?", a: "Nej, Pulse rullar månadsvis utan bindningstid och utan uppsägningstid." },
  { q: "Äger kunden materialet?", a: "Ja, allt slutmaterial ägs av kunden och får användas fritt i kundens egna kanaler." },
  { q: "Vilka kunder har Fenny arbetat med?", a: "Fenny har producerat film för bland andra Garageportexperten, Bolt, Huawei, Renova och Wimeck." },
  { q: "Vad ingår i Fennys produktioner?", a: "Strategi, manus, regi, foto, ljud, klipp, färgsättning och versionering för 16:9, 9:16 och 1:1 görs in-house och ingår i priset." },
  { q: "Var kommer Fennys prisuppgifter ifrån?", a: "Prisspannen bygger på 168 egna offerter och genomförda produktioner, granskade 2026-09-07 av Mattias Björk, och gäller exkl. moms i hela Sverige." },
];
